import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Constants from '../constants/Constants';

const PageContainer = styled.div`
  margin: 0 auto;
  background: #D1CDC8;
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const Title = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 3rem;
  color: #333;
  margin-bottom: 40px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  max-width: 1400px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const CasesList = styled.div`
  background-color: #E7E6E4;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 300px;
  max-height: 80vh;
  overflow-y: auto;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-height: 300px;
  }
`;

const CasesListTitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
`;

const CaseItem = styled.div`
  padding: 12px;
  margin-bottom: 10px;
  background-color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background-color: #f0f0f0;
    transform: translateX(5px);
  }
  
  &.active {
    background-color: #61916e;
    color: white;
  }
`;

const CaseId = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  font-weight: 600;
`;

const DeleteButton = styled.button`
  padding: 4px 8px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c82333;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const FormCard = styled.div`
  background-color: #E7E6E4;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  flex: 1;
  max-height: 80vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-family: 'Roboto', sans-serif;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #61916e;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  min-height: 150px;
  font-family: 'Courier New', monospace;
  
  &:focus {
    outline: none;
    border-color: #61916e;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: #61916e;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-top: 10px;
  
  &:hover:not(:disabled) {
    background-color: #4a7a5a;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &.secondary {
    background-color: #6c757d;
    
    &:hover:not(:disabled) {
      background-color: #5a6268;
    }
  }
  
  &.danger {
    background-color: #dc3545;
    
    &:hover:not(:disabled) {
      background-color: #c82333;
    }
  }
`;

const Message = styled.div`
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
`;

const SuccessMessage = styled(Message)`
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled(Message)`
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Roboto', sans-serif;
  font-size: 1.3rem;
  color: #333;
  margin-top: 30px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #61916e;
`;

const ArrayItem = styled.div`
  background-color: white;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 8px;
  border: 2px solid #ddd;
`;

const ArrayItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ArrayItemTitle = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c82333;
  }
`;

const AddButton = styled(Button)`
  background-color: #28a745;
  
  &:hover:not(:disabled) {
    background-color: #218838;
  }
`;

const NewCaseInput = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const PickImageButton = styled.button`
  padding: 8px 16px;
  background-color: #17a2b8;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: 10px;
  
  &:hover:not(:disabled) {
    background-color: #138496;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: #E7E6E4;
  border-radius: 12px;
  padding: 30px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #61916e;
`;

const ModalTitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 1.8rem;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #ddd;
    color: #333;
  }
`;

const PathSelector = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const PathButton = styled.button`
  padding: 8px 16px;
  background-color: ${props => props.active ? '#61916e' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 2px solid #61916e;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#4a7a5a' : '#f0f0f0'};
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const ImageCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.selected ? '#61916e' : '#ddd'};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    border-color: #61916e;
  }
`;

const ImageThumbnail = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ImageInfo = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 0.85rem;
  color: #333;
  text-align: center;
  word-break: break-all;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-family: 'Roboto', sans-serif;
  font-size: 1.1rem;
`;

function CasesManager() {
  const [cases, setCases] = useState({});
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [caseData, setCaseData] = useState({
    caseId: '',
    title: '',
    area: '',
    form: '',
    images: [],
    rooms: []
  });
  const [password, setPassword] = useState('');
  const [newCaseId, setNewCaseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  
  // Image picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null); // { type: 'image' | 'roomImage', index: number, roomIndex?: number }
  const [pickerImages, setPickerImages] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState(null);
  const [pickerPath, setPickerPath] = useState('');
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  // Load all cases on mount
  useEffect(() => {
    loadCases();
  }, []);

  // Update JSON text when caseData changes (if not in JSON mode)
  useEffect(() => {
    if (!jsonMode && caseData.caseId) {
      setJsonText(JSON.stringify(caseData, null, 2));
    }
  }, [caseData, jsonMode]);

  const loadCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${Constants.get.SERVER_URL}/cases-data`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setCases({});
          return;
        }
        throw new Error(`Failed to load cases: ${response.status}`);
      }
      
      const data = await response.json();
      setCases(data);
    } catch (err) {
      setError(err.message || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadCase = async (caseId) => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      const response = await fetch(`${Constants.get.SERVER_URL}/cases-data/${caseId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load case: ${response.status}`);
      }
      
      const data = await response.json();
      setCaseData(data);
      setSelectedCaseId(caseId);
      setJsonText(JSON.stringify(data, null, 2));
    } catch (err) {
      setError(err.message || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const createNewCase = () => {
    if (!newCaseId.trim()) {
      setError('Please enter a case ID');
      return;
    }
    
    const newCase = {
      caseId: newCaseId.trim(),
      title: '',
      area: '',
      form: '',
      images: [],
      rooms: []
    };
    
    setCaseData(newCase);
    setSelectedCaseId(newCaseId.trim());
    setJsonText(JSON.stringify(newCase, null, 2));
    setNewCaseId('');
    setError(null);
    setMessage(null);
  };

  const saveCase = async () => {
    if (!caseData.caseId) {
      setError('Case ID is required');
      return;
    }
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    let dataToSave;
    if (jsonMode) {
      try {
        dataToSave = JSON.parse(jsonText);
        if (dataToSave.caseId && dataToSave.caseId !== caseData.caseId) {
          setError('caseId in JSON does not match the selected case');
          return;
        }
        if (!dataToSave.caseId) {
          dataToSave.caseId = caseData.caseId;
        }
      } catch (err) {
        setError('Invalid JSON format: ' + err.message);
        return;
      }
    } else {
      dataToSave = { ...caseData };
    }

    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      
      const response = await fetch(
        `${Constants.get.SERVER_URL}/cases-data/${dataToSave.caseId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${password.trim()}`,
          },
          body: JSON.stringify(dataToSave),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(errorData.error || `Save failed with status ${response.status}`);
      }

      const result = await response.json();
      setMessage('Case saved successfully!');
      
      // Reload cases list
      await loadCases();
      
      // Update caseData from saved data
      if (jsonMode) {
        setCaseData(dataToSave);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to save case');
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (caseId) => {
    if (!window.confirm(`Are you sure you want to delete case ${caseId}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      
      const response = await fetch(
        `${Constants.get.SERVER_URL}/cases-data/${caseId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Delete failed' }));
        throw new Error(errorData.error || `Delete failed with status ${response.status}`);
      }

      setMessage(`Case ${caseId} deleted successfully!`);
      
      // Clear selection if deleted case was selected
      if (selectedCaseId === caseId) {
        setSelectedCaseId(null);
        setCaseData({
          caseId: '',
          title: '',
          area: '',
          form: '',
          images: [],
          rooms: []
        });
        setJsonText('');
      }
      
      // Reload cases list
      await loadCases();
      
    } catch (err) {
      setError(err.message || 'Failed to delete case');
    } finally {
      setLoading(false);
    }
  };

  const addImage = () => {
    setCaseData({
      ...caseData,
      images: [...caseData.images, { src: '' }]
    });
  };

  const removeImage = (index) => {
    setCaseData({
      ...caseData,
      images: caseData.images.filter((_, i) => i !== index)
    });
  };

  const updateImage = (index, field, value) => {
    const newImages = [...caseData.images];
    newImages[index] = { ...newImages[index], [field]: value };
    setCaseData({ ...caseData, images: newImages });
  };

  const addRoom = () => {
    setCaseData({
      ...caseData,
      rooms: [...caseData.rooms, {
        roomId: '',
        title: '',
        // status: 'available',
        images: []
      }]
    });
  };

  const removeRoom = (index) => {
    setCaseData({
      ...caseData,
      rooms: caseData.rooms.filter((_, i) => i !== index)
    });
  };

  const updateRoom = (index, field, value) => {
    const newRooms = [...caseData.rooms];
    newRooms[index] = { ...newRooms[index], [field]: value };
    setCaseData({ ...caseData, rooms: newRooms });
  };

  const addRoomImage = (roomIndex) => {
    const newRooms = [...caseData.rooms];
    newRooms[roomIndex].images = [
      ...newRooms[roomIndex].images,
      { src: '' }
    ];
    setCaseData({ ...caseData, rooms: newRooms });
  };

  const removeRoomImage = (roomIndex, imageIndex) => {
    const newRooms = [...caseData.rooms];
    newRooms[roomIndex].images = newRooms[roomIndex].images.filter((_, i) => i !== imageIndex);
    setCaseData({ ...caseData, rooms: newRooms });
  };

  const updateRoomImage = (roomIndex, imageIndex, field, value) => {
    const newRooms = [...caseData.rooms];
    newRooms[roomIndex].images[imageIndex] = {
      ...newRooms[roomIndex].images[imageIndex],
      [field]: value
    };
    setCaseData({ ...caseData, rooms: newRooms });
  };

  const toggleJsonMode = () => {
    if (jsonMode) {
      // Switching from JSON mode to form mode
      try {
        const parsed = JSON.parse(jsonText);
        setCaseData(parsed);
        setJsonMode(false);
      } catch (err) {
        setError('Invalid JSON. Cannot switch to form mode.');
      }
    } else {
      // Switching from form mode to JSON mode
      setJsonText(JSON.stringify(caseData, null, 2));
      setJsonMode(true);
    }
  };

  const openImagePicker = (type, index, roomIndex = null) => {
    if (!selectedCaseId) {
      setError('Please select a case first');
      return;
    }
    setPickerTarget({ type, index, roomIndex });
    setPickerPath(selectedCaseId);
    setPickerOpen(true);
    setSelectedImageUrl(null);
    setPickerError(null);
    loadPickerImages(selectedCaseId);
  };

  const closeImagePicker = () => {
    setPickerOpen(false);
    setPickerTarget(null);
    setPickerImages(null);
    setPickerPath('');
    setSelectedImageUrl(null);
    setPickerError(null);
  };

  const loadPickerImages = async (path) => {
    setPickerLoading(true);
    setPickerError(null);
    
    try {
      const response = await fetch(`${Constants.get.SERVER_URL}/images/${path}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load images' }));
        throw new Error(errorData.error || `Failed to load images: ${response.status}`);
      }
      
      const data = await response.json();
      
      // API returns: { path, count, images: [{ imageId, url }] }
      if (data.images && Array.isArray(data.images)) {
        setPickerImages(data);
      } else {
        setPickerImages({ images: [], path, count: 0 });
      }
    } catch (err) {
      setPickerError(err.message || 'Failed to load images');
      setPickerImages(null);
    } finally {
      setPickerLoading(false);
    }
  };

  const selectImagePath = (path) => {
    setPickerPath(path);
    loadPickerImages(path);
  };

  // Convert /images/ path to /uploads/ path for actual image URLs
  const convertImageUrlToUploads = (url) => {
    if (!url || typeof url !== 'string') return '';
    // Convert /images/... to /uploads/...
    return url;
  };

  const selectImage = (image) => {
    const apiUrl = image.url || image.src || image.path;
    // Convert to /uploads/ format for saving
    const uploadsUrl = convertImageUrlToUploads(apiUrl);
    if (uploadsUrl) {
      setSelectedImageUrl(uploadsUrl);
    }
  };

  const confirmImageSelection = () => {
    if (!selectedImageUrl || !pickerTarget) return;
    
    if (pickerTarget.type === 'image') {
      updateImage(pickerTarget.index, 'src', selectedImageUrl);
    } else if (pickerTarget.type === 'roomImage') {
      updateRoomImage(pickerTarget.roomIndex, pickerTarget.index, 'src', selectedImageUrl);
    }
    
    closeImagePicker();
  };

  // Common paths for image browsing
  const getCommonPaths = () => {
    if (!selectedCaseId) return [];
    const basePaths = [
      selectedCaseId,
    ];
    return basePaths;
  };

  return (
    <PageContainer>
      <Title>Cases Data Manager</Title>
      <ContentContainer>
        <CasesList>
          <CasesListTitle>Cases</CasesListTitle>
          <NewCaseInput>
            <Input
              type="text"
              placeholder="New Case ID (e.g., A0001)"
              value={newCaseId}
              onChange={(e) => setNewCaseId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createNewCase()}
            />
            <Button onClick={createNewCase} style={{ padding: '12px 16px', margin: 0 }}>
              New
            </Button>
          </NewCaseInput>
          <Button
            onClick={loadCases}
            disabled={loading}
            style={{ width: '100%', marginBottom: '20px' }}
            className="secondary"
          >
            {loading ? <LoadingSpinner /> : null}
            Refresh
          </Button>
          {Object.keys(cases).length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              No cases found
            </div>
          )}
          {Object.keys(cases).map((caseId) => (
            <CaseItem
              key={caseId}
              className={selectedCaseId === caseId ? 'active' : ''}
              onClick={() => loadCase(caseId)}
            >
              <CaseId>{caseId}</CaseId>
              <DeleteButton
                onClick={(e) => {
                  e.stopPropagation();
                  deleteCase(caseId);
                }}
                disabled={loading}
              >
                Delete
              </DeleteButton>
            </CaseItem>
          ))}
        </CasesList>

        <FormCard>
          {selectedCaseId ? (
            <>
              <FormGroup>
                <Label>Password (required for save)</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  disabled={loading}
                />
              </FormGroup>

              <Button
                onClick={toggleJsonMode}
                className="secondary"
                disabled={loading}
              >
                {jsonMode ? 'Switch to Form Mode' : 'Switch to JSON Mode'}
              </Button>

              {jsonMode ? (
                <FormGroup>
                  <Label>JSON Data</Label>
                  <TextArea
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    disabled={loading}
                  />
                </FormGroup>
              ) : (
                <>
                  <FormGroup>
                    <Label>Case ID</Label>
                    <Input
                      type="text"
                      value={caseData.caseId}
                      onChange={(e) => setCaseData({ ...caseData, caseId: e.target.value })}
                      disabled={loading}
                    />
                  </FormGroup>

                  <SectionTitle>
                    Images
                    <AddButton onClick={addImage} disabled={loading} style={{ float: 'right', margin: 0 }}>
                      + Add Image
                    </AddButton>
                  </SectionTitle>

                  {caseData.images.map((image, index) => (
                    <ArrayItem key={index}>
                      <ArrayItemHeader>
                        <ArrayItemTitle>Image {index + 1}</ArrayItemTitle>
                        <SmallButton onClick={() => removeImage(index)} disabled={loading}>
                          Remove
                        </SmallButton>
                      </ArrayItemHeader>
                      <FormGroup>
                        <Label>Source</Label>
                        <InputGroup>
                          <Input
                            type="text"
                            value={image.src}
                            onChange={(e) => updateImage(index, 'src', e.target.value)}
                            disabled={loading}
                            placeholder="/images/totorent/A0001/PublicArea/1.jpg"
                            style={{ flex: 1 }}
                          />
                          <PickImageButton
                            onClick={() => openImagePicker('image', index)}
                            disabled={loading || !selectedCaseId}
                          >
                            Pick Image
                          </PickImageButton>
                        </InputGroup>
                      </FormGroup>
                    </ArrayItem>
                  ))}

                  <SectionTitle>
                    Rooms
                    <AddButton onClick={addRoom} disabled={loading} style={{ float: 'right', margin: 0 }}>
                      + Add Room
                    </AddButton>
                  </SectionTitle>

                  {caseData.rooms.map((room, roomIndex) => (
                    <ArrayItem key={roomIndex}>
                      <ArrayItemHeader>
                        <ArrayItemTitle>Room {roomIndex + 1}</ArrayItemTitle>
                        <SmallButton onClick={() => removeRoom(roomIndex)} disabled={loading}>
                          Remove
                        </SmallButton>
                      </ArrayItemHeader>
                      <FormGroup>
                        <Label>Room ID</Label>
                        <Input
                          type="text"
                          value={room.roomId}
                          onChange={(e) => updateRoom(roomIndex, 'roomId', e.target.value)}
                          disabled={loading}
                          placeholder="A, B, C, etc."
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Title</Label>
                        <Input
                          type="text"
                          value={room.title}
                          onChange={(e) => updateRoom(roomIndex, 'title', e.target.value)}
                          disabled={loading}
                          placeholder="RoomA, RoomB, etc."
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>YouTube URL</Label>
                        <Input
                          type="text"
                          value={room.youtube_url || ''}
                          onChange={(e) => updateRoom(roomIndex, 'youtube_url', e.target.value)}
                          disabled={loading}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </FormGroup>
                      {/* <FormGroup>
                        <Label>Status</Label>
                        <Input
                          type="text"
                          value={room.status}
                          onChange={(e) => updateRoom(roomIndex, 'status', e.target.value)}
                          disabled={loading}
                          placeholder="available, rented, etc."
                        />
                      </FormGroup> */}
                      <div style={{ marginTop: '15px' }}>
                        <Label>Room Images</Label>
                        <AddButton
                          onClick={() => addRoomImage(roomIndex)}
                          disabled={loading}
                          style={{ marginTop: '10px' }}
                        >
                          + Add Image
                        </AddButton>
                        {room.images.map((image, imageIndex) => (
                          <ArrayItem key={imageIndex} style={{ marginTop: '10px', backgroundColor: '#f8f9fa' }}>
                            <ArrayItemHeader>
                              <ArrayItemTitle>Image {imageIndex + 1}</ArrayItemTitle>
                              <SmallButton
                                onClick={() => removeRoomImage(roomIndex, imageIndex)}
                                disabled={loading}
                              >
                                Remove
                              </SmallButton>
                            </ArrayItemHeader>
                            <FormGroup>
                              <Label>Source</Label>
                              <InputGroup>
                                <Input
                                  type="text"
                                  value={image.src}
                                  onChange={(e) => updateRoomImage(roomIndex, imageIndex, 'src', e.target.value)}
                                  disabled={loading}
                                  placeholder="/images/totorent/A0001/RoomA/A1.jpg"
                                  style={{ flex: 1 }}
                                />
                                <PickImageButton
                                  onClick={() => openImagePicker('roomImage', imageIndex, roomIndex)}
                                  disabled={loading || !selectedCaseId}
                                >
                                  Pick Image
                                </PickImageButton>
                              </InputGroup>
                            </FormGroup>
                          </ArrayItem>
                        ))}
                      </div>
                    </ArrayItem>
                  ))}
                </>
              )}

              <Button onClick={saveCase} disabled={loading || !password.trim()}>
                {loading && <LoadingSpinner />}
                Save Case
              </Button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              Select a case from the list or create a new one to start editing
            </div>
          )}

          {error && (
            <ErrorMessage>
              <strong>Error:</strong> {error}
            </ErrorMessage>
          )}

          {message && (
            <SuccessMessage>
              <strong>Success:</strong> {message}
            </SuccessMessage>
          )}
        </FormCard>
      </ContentContainer>

      {/* Image Picker Modal */}
      {pickerOpen && (
        <ModalOverlay onClick={closeImagePicker}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Pick Image</ModalTitle>
              <CloseButton onClick={closeImagePicker}>×</CloseButton>
            </ModalHeader>

            <PathSelector>
              <Label style={{ margin: 0, marginRight: '10px' }}>Browse:</Label>
              {getCommonPaths().map((path) => (
                <PathButton
                  key={path}
                  active={pickerPath === path}
                  onClick={() => selectImagePath(path)}
                  disabled={pickerLoading}
                >
                  {path}
                </PathButton>
              ))}
            </PathSelector>

            {pickerLoading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <LoadingSpinner />
                <div style={{ marginTop: '10px', color: '#666' }}>Loading images...</div>
              </div>
            )}

            {pickerError && (
              <ErrorMessage>
                <strong>Error:</strong> {pickerError}
              </ErrorMessage>
            )}

            {pickerImages && !pickerLoading && (
              <>
                {pickerImages.images && pickerImages.images.length > 0 ? (
                  <>
                    <div style={{ marginBottom: '15px', color: '#666' }}>
                      Found {pickerImages.images.length} image(s) in {pickerImages.path || pickerPath}
                    </div>
                    <ImageGrid>
                      {pickerImages.images.map((image, idx) => {
                        console.log('image:', image);
                        const apiUrl = image.url || image.src || image.path;
                        // Convert /images/ to /uploads/ for display and saving
                        const uploadsUrl = convertImageUrlToUploads(apiUrl);
                        const fullUrl = uploadsUrl && uploadsUrl.startsWith('http') 
                          ? uploadsUrl 
                          : uploadsUrl 
                          ? `${Constants.get.SERVER_URL}${uploadsUrl}`
                          : '';
                        const isSelected = selectedImageUrl === uploadsUrl;
                        
                        // Ensure we have a valid string for display
                        const imageIdStr = typeof image.imageId === 'string' 
                          ? image.imageId 
                          : typeof image.id === 'string' 
                          ? image.id 
                          : typeof uploadsUrl === 'string' && uploadsUrl
                          ? uploadsUrl 
                          : `Image ${idx + 1}`;
                        
                        const imageKey = typeof image.imageId === 'string' 
                          ? image.imageId 
                          : typeof image.id === 'string' 
                          ? image.id 
                          : idx;
                        
                        return (
                          <ImageCard
                            key={imageKey}
                            selected={isSelected}
                            onClick={() => selectImage(image)}
                          >
                            <ImageThumbnail
                              src={fullUrl}
                              alt={imageIdStr}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="120"%3E%3Crect fill="%23ddd" width="150" height="120"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            <ImageInfo>{imageIdStr}</ImageInfo>
                          </ImageCard>
                        );
                      })}
                    </ImageGrid>
                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <Button
                        onClick={closeImagePicker}
                        className="secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={confirmImageSelection}
                        disabled={!selectedImageUrl}
                      >
                        Use Selected Image
                      </Button>
                    </div>
                  </>
                ) : (
                  <EmptyState>No images found in this path</EmptyState>
                )}
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default CasesManager;

